/**
 * W-FMCSA's run (ADR-047.2, ADR-047.6): its status, and Run now or a dry run for a Gluon admin, on the whole register or
 * on its first N MCs to try it, with the MCs a sample handled. The run reads FMCSA, then looks each MC in force up on DAT, and goes on in the
 * background: this panel starts it and shows what it last did.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Input from '@mui/joy/Input'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import {
    FMCSA_RUN_MAX_LIMIT,
    isFmcsaRunLimit,
    type BrokerPipelineRunStatus,
    type BrokerPipelineRunSummary,
    type BrokerPipelineRunTrigger,
} from '@numeo/types-core'
import * as React from 'react'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { canRunBrokerRegister } from 'utils/roles'
import { ErrorState, Stat, StatRow, formatNumber } from '../../load-pipeline/components/shared'
import { FmcsaRunMcs } from './FmcsaRunMcs'

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : 'never')

/** The limit typed in: undefined for a whole run, null when the server would refuse it (`isFmcsaRunLimit`). */
function parseLimit(text: string): number | undefined | null {
    if (text.trim() === '') return undefined
    const value = Number(text)
    return isFmcsaRunLimit(value) ? value : null
}

/** "12 min ago", "3 h ago", "2 days ago": how long since a run started. */
function ago(iso: string): string {
    const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000))
    if (minutes < 90) return `${minutes} min ago`
    const hours = Math.round(minutes / 60)
    return hours < 48 ? `${hours} h ago` : `${Math.round(hours / 24)} days ago`
}

/** What the last report was: a whole run, a sample, either as a dry run. */
function reportKind(report: BrokerPipelineRunSummary): string {
    const kind = report.limit ? `sample of ${formatNumber(report.limit)} MCs` : report.mode === 'seed' ? 'seed' : 'whole weekly run'
    return report.dryRun ? `${kind}, dry run` : kind
}

const scope = (limit: number | null | undefined): string => (limit ? `the register's first ${formatNumber(limit)} MCs` : 'the whole register')

const NOT_STARTED: Record<'RUNNING' | 'NOT_DUE', string> = {
    RUNNING: 'A run is already going. Its report appears here when it ends.',
    NOT_DUE: 'Not started: the weekly run is not due yet.',
}

export function FmcsaRunPanel() {
    const [status, setStatus] = React.useState<BrokerPipelineRunStatus | null>(null)
    const [error, setError] = React.useState<unknown>(null)
    const [confirming, setConfirming] = React.useState<'run' | 'dry' | null>(null)
    const [starting, setStarting] = React.useState(false)
    const [answer, setAnswer] = React.useState<BrokerPipelineRunTrigger | null>(null)
    const [limitText, setLimitText] = React.useState('')
    const limit = parseLimit(limitText)
    const canRun = canRunBrokerRegister()

    const refresh = React.useCallback(() => {
        brokerPipelineApi
            .getFmcsaRunStatus()
            .then((next) => {
                setStatus(next)
                setError(null)
            })
            .catch(setError)
    }, [])

    React.useEffect(refresh, [refresh])

    async function start(dryRun: boolean): Promise<void> {
        setConfirming(null)
        setStarting(true)
        try {
            setAnswer(await brokerPipelineApi.startFmcsaRun(dryRun, limit ?? undefined))
            refresh()
        } catch (failure) {
            setError(failure)
        } finally {
            setStarting(false)
        }
    }

    const report = status?.lastReport ?? null
    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography level="title-md">The run</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="plain" onClick={refresh}>
                        Refresh
                    </Button>
                    {canRun ? (
                        <>
                            <Input
                                size="sm"
                                type="number"
                                placeholder="All MCs"
                                value={limitText}
                                onChange={(event) => setLimitText(event.target.value)}
                                error={limit === null}
                                slotProps={{ input: { min: 1, max: FMCSA_RUN_MAX_LIMIT, step: 1, 'aria-label': 'MC limit' } }}
                                sx={{ width: 120 }}
                            />
                            <Button size="sm" variant="outlined" disabled={starting || status?.running || limit === null} onClick={() => setConfirming('dry')}>
                                Dry run
                            </Button>
                            <Button size="sm" loading={starting} disabled={status?.running || limit === null} onClick={() => setConfirming('run')}>
                                Run now
                            </Button>
                        </>
                    ) : null}
                </Box>
            </Box>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 2 }}>
                Weekly, Saturday and Sunday nights Eastern, and on demand here. It reads FMCSA, then DAT for each MC in force, one at a time.
                {canRun
                    ? ` A number limits the run to the register's first MCs, to try it: no CONFIRM, and the weekly run still comes. 1 to ${formatNumber(FMCSA_RUN_MAX_LIMIT)}.`
                    : ' Starting a run takes the gluon_admin role.'}
            </Typography>

            {error ? <ErrorState error={error} /> : null}
            {answer ? (
                <Alert color={answer.started ? 'success' : 'warning'} sx={{ mb: 2 }}>
                    {answer.started
                        ? `Started on ${scope(answer.limit)}${answer.dryRun ? ' as a dry run: it publishes nothing' : ''}. It runs in the background.`
                        : NOT_STARTED[answer.because]}
                </Alert>
            ) : null}

            {status ? (
                <StatRow>
                    <Stat
                        label="Now"
                        value={status.running ? 'Running' : 'Idle'}
                        tone={status.running ? 'warning' : 'neutral'}
                        hint={`last whole run started ${when(status.lastStartedAt)}`}
                    />
                    <Stat
                        label="Last whole run completed"
                        value={when(status.lastCompletedAt)}
                        hint="what makes the week's run done; a sample, a dry run or a failed run records none"
                    />
                    {report ? (
                        <>
                            <Stat
                                label="Last run published"
                                value={formatNumber(report.state + report.confirm)}
                                hint={`${report.startedAt ? `${ago(report.startedAt)}, ` : ''}${reportKind(report)}: ${formatNumber(report.state)} STATE, ${formatNumber(report.confirm)} CONFIRM, ${formatNumber(report.unchanged)} unchanged of ${formatNumber(report.brokerages)} MCs`}
                            />
                            <Stat
                                label="Its DAT step"
                                value={report.dat?.endState === 'DISABLED' ? 'Off' : (report.dat?.endState ?? '—')}
                                tone={report.dat?.endState === 'FAILED' ? 'danger' : 'neutral'}
                                hint={
                                    !report.dat
                                        ? 'no DAT step in this report'
                                        : report.dat.endState === 'DISABLED'
                                          ? 'the live DAT request is off in code until DAT contracts with and whitelists Numeo (ADR-047.6): DAT was not asked'
                                          : `${formatNumber(report.dat.looked)} looked up, ${formatNumber(report.dat.listed)} listed, ${formatNumber(report.dat.published)} published`
                                }
                            />
                        </>
                    ) : null}
                </StatRow>
            ) : null}
            {report?.mcs ? <FmcsaRunMcs mcs={report.mcs} dryRun={report.dryRun} /> : null}

            <Modal open={confirming !== null} onClose={() => setConfirming(null)}>
                <ModalDialog>
                    <Typography level="title-md">{confirming === 'dry' ? 'Start a dry run?' : 'Start the run now?'}</Typography>
                    <Typography level="body-sm">
                        {confirming === 'dry'
                            ? `On ${scope(limit)}: it reads FMCSA and asks DAT as a real run does, and publishes nothing.`
                            : `On ${scope(limit)}: it reads FMCSA, publishes what changed, then asks DAT about each MC in force that is due.`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="plain" color="neutral" onClick={() => setConfirming(null)}>
                            Cancel
                        </Button>
                        <Button onClick={() => void start(confirming === 'dry')}>Start</Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </Sheet>
    )
}
